import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Users, Calendar, BedDouble, Receipt, TrendingUp, Activity, Pill, FlaskConical, Clock, DollarSign, FileText, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, Legend } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';
import SmartSearch from '@/components/SmartSearch';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);
  const [appointmentData, setAppointmentData] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState([]);

  // Handle search result click - navigate to appropriate page
  const handleSearchResultClick = (result) => {
    switch (result.type) {
      case 'patient':
        navigate(`/patients?id=${result.id}`);
        break;
      case 'appointment':
        navigate(`/appointments?id=${result.id}`);
        break;
      case 'prescription':
        navigate(`/prescriptions?id=${result.id}`);
        break;
      case 'lab_order':
        navigate(`/lab-orders?id=${result.id}`);
        break;
      case 'visit':
        navigate(`/visit-management?id=${result.id}`);
        break;
      case 'billing':
        navigate(`/billing?id=${result.id}`);
        break;
      default:
        toast.info(`Viewing ${result.type}: ${result.name || result.id}`);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchAnalytics();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      // Fetch billing data for revenue chart
      const billingRes = await api.get('/billing');
      const bills = billingRes.data;
      
      // Calculate monthly revenue (last 6 months)
      const monthlyRevenue = {};
      const today = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const key = d.toLocaleString('default', { month: 'short' });
        monthlyRevenue[key] = { month: key, revenue: 0, collected: 0 };
      }
      
      bills.forEach(bill => {
        if (bill.created_at) {
          const date = new Date(bill.created_at);
          const key = date.toLocaleString('default', { month: 'short' });
          if (monthlyRevenue[key]) {
            monthlyRevenue[key].revenue += bill.total_amount || 0;
            monthlyRevenue[key].collected += bill.paid_amount || 0;
          }
        }
      });
      setRevenueData(Object.values(monthlyRevenue));
      
      // Calculate bill status distribution
      const statusCounts = {};
      bills.forEach(bill => {
        statusCounts[bill.status] = (statusCounts[bill.status] || 0) + 1;
      });
      setStatusDistribution(Object.entries(statusCounts).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value })));
      
      // Fetch appointments for appointment analytics
      const apptRes = await api.get('/appointments');
      const appointments = apptRes.data;
      
      // Daily appointments for last 7 days
      const dailyAppts = {};
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const key = days[d.getDay()];
        dailyAppts[key] = { day: key, scheduled: 0, completed: 0 };
      }
      
      appointments.forEach(appt => {
        if (appt.date) {
          const date = new Date(appt.date);
          const dayDiff = Math.floor((today - date) / (1000 * 60 * 60 * 24));
          if (dayDiff >= 0 && dayDiff < 7) {
            const key = days[date.getDay()];
            if (dailyAppts[key]) {
              dailyAppts[key].scheduled++;
              if (appt.status === 'completed') {
                dailyAppts[key].completed++;
              }
            }
          }
        }
      });
      setAppointmentData(Object.values(dailyAppts));
      
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const chartData = [
    { name: 'Mon', patients: 45 },
    { name: 'Tue', patients: 52 },
    { name: 'Wed', patients: 48 },
    { name: 'Thu', patients: 61 },
    { name: 'Fri', patients: 55 },
    { name: 'Sat', patients: 38 },
    { name: 'Sun', patients: 25 },
  ];

  const COLORS = ['#4A238F', '#0f766e', '#ca8a04', '#dc2626', '#7c3aed'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Patients',
      value: stats?.total_patients || 0,
      icon: Users,
      color: 'text-[#4A238F]',
      bgColor: 'bg-indigo-50',
      change: '+12%',
      changeType: 'up'
    },
    {
      title: 'Appointments Today',
      value: stats?.total_appointments || 0,
      icon: Calendar,
      color: 'text-[#0f766e]',
      bgColor: 'bg-teal-50',
      change: '+8%',
      changeType: 'up'
    },
    {
      title: 'Active Admissions',
      value: stats?.total_admissions || 0,
      icon: BedDouble,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      change: '-3%',
      changeType: 'down'
    },
    {
      title: 'Pending Bills',
      value: stats?.pending_bills || 0,
      icon: Receipt,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      change: '+5%',
      changeType: 'up'
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-slate-600 mt-2">Overview of hospital operations and key metrics</p>
        </div>

        {/* Smart Search */}
        <div className="mb-8">
          <SmartSearch 
            onResultClick={handleSearchResultClick}
            className="max-w-3xl"
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all hover:border-purple-100">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600 mb-1">{stat.title}</p>
                      <p className="text-2xl md:text-3xl font-bold text-slate-900">{stat.value}</p>
                      <div className={`flex items-center gap-1 mt-1 text-xs ${stat.changeType === 'up' ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {stat.changeType === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        <span>{stat.change} vs last week</span>
                      </div>
                    </div>
                    <div className={`p-2 md:p-3 rounded-md ${stat.bgColor}`}>
                      <stat.icon className={`h-5 w-5 md:h-6 md:w-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* Revenue Chart */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Revenue Overview (6 Months)
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4A238F" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4A238F" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0f766e" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#0f766e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} tickFormatter={(value) => `$${value}`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`$${value.toFixed(2)}`, '']}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" name="Billed" stroke="#4A238F" fillOpacity={1} fill="url(#colorRevenue)" />
                  <Area type="monotone" dataKey="collected" name="Collected" stroke="#0f766e" fillOpacity={1} fill="url(#colorCollected)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Appointments Chart */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#0f766e]" />
                  Appointments (Last 7 Days)
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={appointmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Bar dataKey="scheduled" name="Scheduled" fill="#4A238F" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed" name="Completed" fill="#0f766e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Patient Visits Chart */}
          <Card className="border border-slate-200 shadow-sm lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#4A238F]" />
                  Patient Visits This Week
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                  />
                  <Line type="monotone" dataKey="patients" stroke="#4A238F" strokeWidth={2} dot={{ fill: '#4A238F', r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Billing Status Distribution */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-amber-600" />
                  Billing Status
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions / System Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mt-6">
          {/* System Status */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#4A238F]" />
                  System Status
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { label: 'Database', status: 'Operational', color: 'emerald' },
                { label: 'API Services', status: 'Operational', color: 'emerald' },
                { label: 'Notification System', status: 'Operational', color: 'emerald' },
                { label: 'Backup System', status: 'Scheduled', color: 'blue' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full bg-${item.color}-500`} />
                    <span className={`text-sm text-${item.color}-600`}>{item.status}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  );
}